// ── Sound file manifest ──────────────────────────────────────────────────────
// Single source of truth for every audio file the sim plays. Shared between:
//   • AssetManager — decodes each path into a cached AudioBuffer (tracked by the
//     loading progress bar) once at startup.
//   • AudioManager — binds those cached buffers to THREE.Audio playback voices
//     per scenario, using the categorized lists below for its mixing logic.
//
// Paths are relative to the public root. Buffers are keyed by `name`.

import { AUDIO, JTAC } from '../core/SimConfig.js';

const EXT_PATH = 'assets/sound/';
const CPT_PATH = 'assets/sound/cockpit/';
const MECH_PATH = 'assets/sound/';

// Public URL for a manifest-style path (relative to the public root). The
// production build re-encodes every .wav to a compressed format and removes
// the original (tools/vite-plugin-optimize-assets.mjs), so .wav paths are
// rewritten to whatever extension that build shipped; anything else passes
// through. Every .wav URL the client builds should come through here.
export function audioUrl(path) {
  const url = import.meta.env.BASE_URL + path;
  return __WAV_EXT__ === '.wav' ? url : url.replace(/\.wav$/i, __WAV_EXT__);
}

// The compressed build pads each file's tail out to a whole codec frame —
// silence that a looping buffer would play at every seam. The build records
// each source WAV's exact duration (`__WAV_DURATIONS__`, null in dev) and this
// cuts a decoded buffer back to it. Returns the buffer untouched when there's
// nothing to trim.
export function trimToSource(path, buffer) {
  const duration = __WAV_DURATIONS__?.[path];
  if (!duration) return buffer;
  const length = Math.round(duration * buffer.sampleRate);
  if (length >= buffer.length) return buffer;

  const out = new AudioBuffer({ length, numberOfChannels: buffer.numberOfChannels, sampleRate: buffer.sampleRate });
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    out.copyToChannel(buffer.getChannelData(c).subarray(0, length), c);
  }
  return out;
}

// ── Engine loops / one-shots (camera-blended, directional) ──────────────────
export const LOOP_EXT = [
  'Idle', 'Idle_Front', 'Idle_Back',
  'Maximum_Turbine', 'Maximum_Fan', 'Maximum', 'Maximum_Front',
  'Distant', 'Distant_Front', 'Distant_Back',
  'Flyby',
];

export const LOOP_CPT = [
  'Idle_cockpit', 'Maximum_cockpit',
  'Avionics_cockpit', 'Wind_cockpit', 'Turbulence_cockpit',
  'Ground_cockpit',
];

export const ONESHOT_EXT = [
  'Startup', 'Startup_Front', 'Startup_Back',
  'Shutdown', 'Shutdown_Front', 'Shutdown_Back',
];

export const ONESHOT_CPT = [
  'Startup_cockpit', 'Shutdown_cockpit',
];

// ── Mechanical cockpit sounds (flaps, gear) ─────────────────────────────────
export const MECH_SOUNDS = {
  Flaps:      { file: 'Flaps.wav',      loop: false },
  Gear_Start: { file: 'Gear_Start.wav', loop: false },
  Gear_Loop:  { file: 'Gear_Loop.wav',  loop: true  },
  Gear_End:   { file: 'Gear_End.wav',   loop: false },
};

// ── Gun sounds (cockpit + external, blended by camera) ──────────────────────
export const GUN_SOUNDS = {
  Gun_Loop:          { path: CPT_PATH + 'Gun_Loop.wav',          loop: true  },
  Gun_End:           { path: CPT_PATH + 'Gun_End.wav',           loop: false },
  Gun_Loop_External: { path: EXT_PATH + 'Gun_Loop_External.wav', loop: true  },
  Gun_End_External:  { path: EXT_PATH + 'Gun_End_External.wav',  loop: false },
};

// ── Weapon one-shots (camera-independent) ───────────────────────────────────
export const WEAPON_ONESHOTS = {
  AIM9_Launch:         { path: EXT_PATH + 'AIM-9_Launch.wav', loop: false },
  AIM9_Launch_Cockpit: { path: EXT_PATH + 'AIM-9_Launch_Cockpit.wav', loop: false },
};

// ── Weapon loops (avionics tones — camera-independent, heard via headset) ───
export const WEAPON_LOOPS = {
  AIM9_Growl: { path: EXT_PATH + 'AIM-9_Growl.wav', loop: true },
  AIM9_Lock:  { path: EXT_PATH + 'AIM-9_Lock.wav',  loop: true },
};

// ── Radar Warning Receiver one-shots (polyphonic) + tracking loop ───────────
export const RWR_SOUNDS = {
  RWR_Contact: { file: 'RWR_Contact.wav' },
  RWR_Blip:    { file: 'RWR_Blip.wav' },
  RWR_Launch:  { file: 'RWR_Launch.wav' },
};
export const RWR_LOOP_NAME = 'RWR_Tracking_Loop';

// ── Voice Warning System one-shots (GPWS-style spoken cautions) ─────────────
export const VWS_SOUNDS = {
  VWS_Altitude: { file: 'VWS_Altitude.wav' },
  VWS_Pullup:   { file: 'VWS_Pullup.wav' },
};
// Stall buffet warning — a continuous loop (not a spoken one-shot) that runs
// while the wing is past its stall angle, bound like the RWR tracking loop.
export const VWS_STALL_LOOP_NAME = 'VWS_Stall';
// Master caution — battle-damage warning that loops continuously while the A-10
// has a knocked-out engine, bound like the stall loop.
export const VWS_MASTER_LOOP_NAME = 'VWS_Master';

// ── Distant-aircraft proximity loop ─────────────────────────────────────────
// A second playback voice bound to the existing Distant.wav buffer (NOT a new
// file — intentionally absent from ALL_SOUNDS). AudioManager drives its gain
// from the cumulative proximity of every jet in range; separate from the A-10's
// own engine Distant layer so the two can sound independently.
export const DISTANT_JETS_NAME = 'Distant_Jets';

// ── Enemy proximity loops ───────────────────────────────────────────────────
// One shared, non-positional loop per enemy type (not one voice per entity):
// AudioManager sums the proximity of every live enemy carrying the matching
// `audioLoop` tag and drives a single voice from that total — so a column of
// BTRs next to the camera is simply loud, with no directional imaging. The keys
// here are the buffer names referenced by `AUDIO.enemyLoops[*].sound`.
export const ENEMY_LOOPS = {
  Helicopter_Loop:      { path: EXT_PATH + 'Helicopter_Loop.wav' },
  Vehicle_Driving_Loop: { path: EXT_PATH + 'Vehicle_Driving_Loop.wav' },
  Vehicle_Idle_Loop:    { path: EXT_PATH + 'Vehicle_Idle_Loop.wav' },
};

// ── Ambient environment loop ────────────────────────────────────────────────
// Continuous outdoor bed (wind/atmosphere). Camera-independent; AudioManager
// drives its volume from the environmental wind speed + camera blend.
export const AMBIENT_LOOP_NAME = 'Ambient_Loop';

// ── Flight-school instructor voice-over ─────────────────────────────────────
// Deliberately absent from ALL_SOUNDS: the VO is per-training-mission (only one
// scenario ever needs it), it's minutes of speech rather than short samples, and
// it's played through plain HTMLAudioElements by js/training/CaptionPlayer.js —
// streamed, not decoded up front — so it neither bloats the startup decode nor
// rides the per-scenario Web Audio teardown. Only the naming convention lives
// here, so every audio path in the sim is still findable from this file.
//
//   assets/sound/voice/<mission>/<mission>_<key>.mp3
//
// where <key> is `<objective>_captions`, `<objective>_complete`, or
// `<objective>_hint_<hintId>` — see js/training/missions/ for the authoring end.
const VOICE_PATH = 'assets/sound/voice/';

export function trainingVoiceUrl(mission, key) {
  if (!mission || !key) return null;
  return `${import.meta.env.BASE_URL}${VOICE_PATH}${mission}/${mission}_${key}.mp3`;
}

// ── Mission radio (tower + JTAC) ──────────────────────────────────
// Real radio recordings, played procedurally by js/mission/JTACRadio.js — field
// tower on one net, the ground controller on the other, one folder each.
// Deliberately absent from ALL_SOUNDS for the same reasons as the instructor VO
// above: speech rather than short samples, wanted only by some scenarios, and
// streamed through HTMLAudioElements — so it neither bloats the startup decode
// nor rides the per-scenario Web Audio teardown. The clip ids are filename
// stems; `JTAC.cues` is what maps a cue onto the recordings that can serve it,
// and a cue's `net` picks the folder below.
//
//   assets/sound/<net>/<clip><JTAC.fileExt>
const RADIO_PATH = {
  atc:  'assets/sound/atc/',
  jtac: 'assets/sound/jtac/',
};

export function radioClipUrl(clip, net = 'jtac') {
  const dir = RADIO_PATH[net];
  if (!clip || !dir) return null;
  return audioUrl(`${dir}${clip}${JTAC.fileExt}`);
}

// ── Unified load list: every file, keyed by name, with its path + loop flag ──
// AssetManager iterates this to decode + cache buffers. The `loop` flag is
// advisory for AudioManager's THREE.Audio voices; polyphonic groups (explosion,
// RWR one-shots) ignore it and read raw buffers instead.
export const ALL_SOUNDS = [
  ...LOOP_EXT.map(name => ({ name, path: EXT_PATH + name + '.wav', loop: true })),
  ...LOOP_CPT.map(name => ({ name, path: CPT_PATH + name + '.wav', loop: true })),
  ...ONESHOT_EXT.map(name => ({ name, path: EXT_PATH + name + '.wav', loop: false })),
  ...ONESHOT_CPT.map(name => ({ name, path: CPT_PATH + name + '.wav', loop: false })),
  ...Object.entries(MECH_SOUNDS).map(([name, cfg]) => ({ name, path: MECH_PATH + cfg.file, loop: cfg.loop })),
  ...Object.entries(GUN_SOUNDS).map(([name, cfg]) => ({ name, path: cfg.path, loop: cfg.loop })),
  ...Object.entries(WEAPON_ONESHOTS).map(([name, cfg]) => ({ name, path: cfg.path, loop: cfg.loop })),
  ...Object.entries(WEAPON_LOOPS).map(([name, cfg]) => ({ name, path: cfg.path, loop: cfg.loop })),
  ...Object.entries(ENEMY_LOOPS).map(([name, cfg]) => ({ name, path: cfg.path, loop: true })),
  ...Object.entries(AUDIO.explosions.sounds).map(([name, cfg]) => ({ name, path: EXT_PATH + cfg.file, loop: false })),
  ...Object.entries(RWR_SOUNDS).map(([name, cfg]) => ({ name, path: EXT_PATH + cfg.file, loop: false })),
  ...Object.entries(VWS_SOUNDS).map(([name, cfg]) => ({ name, path: EXT_PATH + cfg.file, loop: false })),
  { name: RWR_LOOP_NAME, path: EXT_PATH + 'RWR_Tracking_Loop.wav', loop: true },
  { name: VWS_STALL_LOOP_NAME, path: EXT_PATH + 'VWS_Stall.wav', loop: true },
  { name: VWS_MASTER_LOOP_NAME, path: EXT_PATH + 'VWS_Master_Loop.wav', loop: true },
  { name: AMBIENT_LOOP_NAME, path: EXT_PATH + 'Ambient_Loop.wav', loop: true },
];
